import type { PatternProject } from '../types';

export type PatternAxis = 'row' | 'column';
export type PatternLineAction = 'insert' | 'delete';

export interface PatternLineStats {
  beadCount: number;
  completedCount: number;
}

export interface PatternLineAdjustment {
  project: PatternProject;
  removedBeads: number;
  removedCompleted: number;
}

function axisLength(project: PatternProject, axis: PatternAxis) {
  return axis === 'row' ? project.height : project.width;
}

function assertRange(project: PatternProject, axis: PatternAxis, action: PatternLineAction, index: number, count: number) {
  const length = axisLength(project, axis);
  const maximumIndex = action === 'insert' ? length : length - count;
  if (!Number.isInteger(index) || !Number.isInteger(count) || count < 1 || index < 0 || index > maximumIndex) {
    throw new RangeError(`Invalid ${axis} ${action} range: ${index} + ${count}`);
  }
}

export function getPatternLineStats(project: PatternProject, axis: PatternAxis, index: number, count = 1): PatternLineStats {
  assertRange(project, axis, 'delete', index, count);
  let beadCount = 0;
  let completedCount = 0;
  for (let lineOffset = 0; lineOffset < count; lineOffset += 1) {
    const cells = axis === 'row' ? project.width : project.height;
    for (let offset = 0; offset < cells; offset += 1) {
      const cellIndex = axis === 'row'
        ? (index + lineOffset) * project.width + offset
        : offset * project.width + index + lineOffset;
      if (!project.empty[cellIndex]) {
        beadCount += 1;
        if (project.completed[cellIndex]) completedCount += 1;
      }
    }
  }
  return { beadCount, completedCount };
}

export function adjustPatternLine(
  project: PatternProject,
  axis: PatternAxis,
  action: PatternLineAction,
  index: number,
  count = 1
): PatternLineAdjustment {
  assertRange(project, axis, action, index, count);
  const inserting = action === 'insert';
  const delta = inserting ? count : -count;
  const nextWidth = project.width + (axis === 'column' ? delta : 0);
  const nextHeight = project.height + (axis === 'row' ? delta : 0);
  const colorIndices = new Uint16Array(nextWidth * nextHeight);
  const empty = new Uint8Array(nextWidth * nextHeight);
  const completed = new Uint8Array(nextWidth * nextHeight);
  empty.fill(1);

  for (let row = 0; row < project.height; row += 1) {
    if (!inserting && axis === 'row' && row >= index && row < index + count) continue;
    const nextRow = axis === 'row' && row >= index
      ? row + (inserting ? count : -count)
      : row;
    for (let col = 0; col < project.width; col += 1) {
      if (!inserting && axis === 'column' && col >= index && col < index + count) continue;
      const nextCol = axis === 'column' && col >= index
        ? col + (inserting ? count : -count)
        : col;
      const from = row * project.width + col;
      const to = nextRow * nextWidth + nextCol;
      colorIndices[to] = project.colorIndices[from];
      empty[to] = project.empty[from];
      completed[to] = project.completed[from];
    }
  }

  const removed = inserting ? { beadCount: 0, completedCount: 0 } : getPatternLineStats(project, axis, index, count);
  return {
    project: {
      ...project,
      width: nextWidth,
      height: nextHeight,
      colorIndices,
      empty,
      completed,
      updatedAt: Date.now()
    },
    removedBeads: removed.beadCount,
    removedCompleted: removed.completedCount
  };
}
